import { useBusinessEntitiesOfCompany } from '@commenda/api';
import {
  Button,
  EmptyState,
  Icons,
  Skeleton,
  useToggle,
} from '@commenda/kakashi';
import { AddEntityModal } from '@commenda/ui';
import { CorporationType } from '@prisma/client';
import { useEntitiesGridView } from 'hooks/useEntitiesGridView';
import { useRouter } from 'next/router';
import { EntityCard } from './EntityCard';

const MAX_VISIBLE_ENTITIES = 3;

const formatCorporationType = (
  type?: CorporationType | null
): string | undefined => {
  if (!type || type === CorporationType.Unknown) return undefined;

  const specialCases: Partial<Record<CorporationType, string>> = {
    [CorporationType.PteLtd]: 'Pte Ltd',
    [CorporationType.PvtLtd]: 'Pvt Ltd',
    [CorporationType.CCorp]: 'C Corp',
  };

  const mapped = specialCases[type];
  if (mapped) return mapped;

  return type.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const EntitiesSection = ({ companyId }: { companyId: number }) => {
  const router = useRouter();

  const addEntityModal = useToggle();

  const { data: businessEntitiesResponse, refetch: refreshBusinessEntities } =
    useBusinessEntitiesOfCompany(companyId);

  const businessEntities = businessEntitiesResponse?.entities ?? [];

  const { businessEntities: entitiesView } = useEntitiesGridView(businessEntities);

  if (!businessEntitiesResponse) {
    return (
      <div className="flex flex-col gap-md pb-xl">
        <div className="flex justify-end px-xl">
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex gap-md px-xl">
          <Skeleton className="h-24 flex-1 rounded-xl" />
          <Skeleton className="h-24 flex-1 rounded-xl" />
          <Skeleton className="h-24 flex-1 rounded-xl" />
        </div>
      </div>
    );
  }

  if (entitiesView.length === 0) {
    return (
      <div className="flex flex-col gap-2xl px-xl pb-xl">
        <AddEntityModal
          companyId={+companyId}
          isOpen={addEntityModal.visible}
          onClose={addEntityModal.off}
          onSuccess={async () => {
            await refreshBusinessEntities();
            addEntityModal.off();
          }}
        />
        <EmptyState
          heading="No entities added"
          description="Add your first entity to get started."
          primaryAction={{
            label: 'Add an entity',
            onClick: addEntityModal.show,
            variant: 'secondary',
            leftIcon: <Icons.Plus className="size-4" />,
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-md pb-xl">
      <AddEntityModal
        companyId={+companyId}
        isOpen={addEntityModal.visible}
        onClose={addEntityModal.off}
        onSuccess={async () => {
          await refreshBusinessEntities();
          addEntityModal.off();
        }}
      />

      <div className="flex items-center justify-end px-xl">
        {entitiesView.length > MAX_VISIBLE_ENTITIES && (
          <Button
            variant="brand-link"
            size="sm"
            onClick={() => router.push('/corporations')}
          >
            View all entities
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-md px-xl">
        {entitiesView.slice(0, MAX_VISIBLE_ENTITIES).map((entity, idx) => (
          <EntityCard
            key={entity.name || idx}
            name={entity.name}
            incorporationCountry={entity.incorporationCountry}
            statusText={
              entity.tooltip?.text ??
              formatCorporationType(entity.corporationType as CorporationType)
            }
            link={entity.link}
          />
        ))}
      </div>
    </div>
  );
};
